import React, { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SUPPLIER_PUBLIC_NOT_AVAILABLE_SUBTITLE,
  SUPPLIER_PUBLIC_NOT_AVAILABLE_TITLE,
} from 'modules/FreeSupplier/supplierPublicPresentation';
import { createPostMessageBridge } from 'modules/Embed/postMessageBridge';
import { usePublicPortalAccess } from 'modules/PublicCatalog/hooks/useFetchPublicCatalogShare';
import { useBranding } from 'modules/App/hooks/useBranding';
import { accentCssVars, normalizeHexColor } from 'modules/Common/utils/brandColor';
import PortalPasswordGate from 'modules/PublicCatalog/components/PortalPasswordGate';
import { CgSkeletonBar } from 'modules/Common/ui/CgSkeletons';
import PortalWidget from '../components/PortalWidget';

// The widget renders inside a size-reporting iframe on someone else's page —
// its height is broadcast to the host via the postMessage bridge. A page-sized
// loader stage would therefore make the EMBEDDING site's frame balloon and then
// snap back, so the loading state mirrors PortalWidget's own compact card
// (brand strip, identity header, `rows` product rows) at close to its final
// height. `theme` is already resolved before the payload lands, so the strip is
// on-brand from the first frame.
function PortalWidgetSkeleton({ theme, rows = 2 }) {
  const { t } = useTranslation('freeSupplier');
  return (
    <div
      className="bg-white rounded-2xl border shadow-sm w-full max-w-md overflow-hidden mx-auto"
      style={{ borderColor: `${theme}40` }}
      aria-label={t('freeSupplier:publicWidget.loadingAriaLabel', 'Loading emissions data')}
      role="status"
    >
      <div
        className="h-1 w-full"
        style={{ background: theme }}
      />
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div
          className="w-9 h-9 rounded-lg border border-slate-200 shrink-0 animate-pulse"
          style={{ background: `${theme}14` }}
        />
        <div className="flex-1 min-w-0">
          <CgSkeletonBar className="h-2 w-16" />
          <CgSkeletonBar className="h-3 w-32 max-w-full mt-1.5" />
        </div>
      </div>
      <div className="border-t border-slate-100">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="px-4 py-2.5 border-b border-slate-100 last:border-b-0"
          >
            <CgSkeletonBar className="h-2.5 w-28 max-w-full" />
            <div className="flex items-center gap-3 mt-2">
              <CgSkeletonBar className="h-2 w-12" />
              <CgSkeletonBar className="h-2 w-12" />
              <CgSkeletonBar className="h-2 w-14" />
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 py-2">
        <CgSkeletonBar className="h-2 w-24" />
      </div>
    </div>
  );
}

// The chrome-less embeddable widget route
// (/public/cbam-widget/:slug). This is what EmbedWidgetPage's live
// preview iframe and any third-party embed snippet actually load. Cosmetic
// query params follow the EmbedPortalWidgetPage prototype convention —
// primaryColor (hex without #), layout, showFactors — honored client-side
// only, never sent to the backend.
export default function PublicSupplierWidgetPage() {
  const { t } = useTranslation('freeSupplier');
  const { slug } = useParams();
  const { search } = useLocation();
  const containerRef = useRef(null);

  const params = new URLSearchParams(search);
  const urlColor = params.get('primaryColor');
  const layout = params.get('layout') || 'card';
  const showFactors = params.get('showFactors') !== 'false';
  const brand = useBranding();

  const { metaQuery, payload, passwordError, dataMutation, fatalError, submitPassword } =
    usePublicPortalAccess(slug, {
      surface: 'widget',
      passwordErrorMessage: t(
        'freeSupplier:publicWidget.passwordError',
        'That password didn’t work. Please try again.',
      ),
    });

  // Theme precedence (D50): an explicit ?primaryColor embed override → the
  // supplier's persisted color (already resolved server-side to their choice or
  // the deployment/whitelabel brand default) → the running deployment brand
  // (covers the moment before the payload lands) → CG green.
  const theme =
    normalizeHexColor(urlColor) ||
    normalizeHexColor(payload?.operator?.primaryColor) ||
    normalizeHexColor(brand.primaryColor) ||
    '#00B894';

  // PortalWidget themes its own elements via the `theme` prop (inline styles),
  // but nested shared components (e.g. CgCnGoodHoverText's CN-code link) read
  // the `--primary-color` CSS var. Scope both vars to the resolved theme on the
  // widget root so every descendant — inline-styled or class-based — matches.
  const themeVars = accentCssVars(theme);

  // S2: the canonical cg-embed bridge (modules/Embed/postMessageBridge.js) —
  // announce readiness once mounted, then keep the parent informed of our
  // height via ResizeObserver so the host page can auto-size the iframe.
  // Gains version/dedupe(only emits on an actual height change)/origin/error
  // handling over the previous hand-rolled postMessage call. No parentOrigin
  // is passed — D34's "any HTTPS origin, no allow-list" ruling means we don't
  // know the embedding site in advance; the bridge falls back to
  // document.referrer's origin when available, else '*'.
  useEffect(() => {
    const bridge = createPostMessageBridge();
    bridge.emitReady();
    const node = containerRef.current;
    if (!node) return undefined;
    const stopObserving = bridge.observeResize(node);
    return () => {
      stopObserving();
      bridge.destroy();
    };
  }, [metaQuery.isLoading, metaQuery.isError, dataMutation.isError, payload]);

  return (
    <div
      ref={containerRef}
      className="p-4 bg-transparent"
      style={themeVars}
    >
      {/* Show loading whenever we're still waiting for a payload and there's no
          error/password gate to show — this includes the frame between meta
          resolving (requiresPassword:false) and the auto-POST firing in a
          post-paint effect, which previously flashed blank (G4). */}
      {!fatalError && !payload && !metaQuery.data?.requiresPassword && (
        <PortalWidgetSkeleton
          theme={theme}
          rows={layout === 'compact' ? 2 : 3}
        />
      )}
      {!metaQuery.isLoading && fatalError && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm w-full max-w-md mx-auto px-4 py-8 text-center">
          <p className="text-sm font-semibold text-slate-600 m-0">
            {SUPPLIER_PUBLIC_NOT_AVAILABLE_TITLE}
          </p>
          <p className="text-xs text-slate-400 m-0 mt-1">
            {SUPPLIER_PUBLIC_NOT_AVAILABLE_SUBTITLE}
          </p>
        </div>
      )}
      {!metaQuery.isLoading && !fatalError && !payload && metaQuery.data?.requiresPassword && (
        <PortalPasswordGate
          operatorName={metaQuery.data.operatorName}
          operatorLogoUrl={metaQuery.data.operatorLogoUrl}
          loading={dataMutation.isPending}
          error={passwordError}
          onSubmit={submitPassword}
        />
      )}
      {!metaQuery.isLoading && !fatalError && payload && (
        <PortalWidget
          payload={payload}
          theme={theme}
          layout={layout}
          showFactors={showFactors}
        />
      )}
    </div>
  );
}
